import React, { createContext, useContext, useState, ReactNode } from 'react';

// Only account-level sections (no business settings)
export type SettingsSection = 'profile' | 'plan' | 'security';

interface AccountSettingsContextType {
  isAccountSettingsOpen: boolean;
  activeSection: SettingsSection;
  toggleAccountSettings: () => void;
  openAccountSettings: (section?: SettingsSection) => void;
  closeAccountSettings: () => void;
  setActiveSection: (section: SettingsSection) => void;
}

const AccountSettingsContext = createContext<AccountSettingsContextType | undefined>(undefined);

export const useAccountSettings = () => {
  const context = useContext(AccountSettingsContext);
  if (context === undefined) {
    throw new Error('useAccountSettings must be used within an AccountSettingsProvider');
  }
  return context;
};

interface AccountSettingsProviderProps {
  children: ReactNode;
}

export const AccountSettingsProvider: React.FC<AccountSettingsProviderProps> = ({ children }) => {
  const [isAccountSettingsOpen, setIsAccountSettingsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<SettingsSection>('profile');

  const toggleAccountSettings = () => {
    setIsAccountSettingsOpen(prev => !prev);
  };

  const openAccountSettings = (section?: SettingsSection) => {
    if (section) {
      setActiveSection(section);
    }
    setIsAccountSettingsOpen(true);
  };

  const closeAccountSettings = () => {
    setIsAccountSettingsOpen(false);
  };

  return (
    <AccountSettingsContext.Provider value={{
      isAccountSettingsOpen,
      activeSection,
      toggleAccountSettings,
      openAccountSettings,
      closeAccountSettings,
      setActiveSection
    }}>
      {children}
    </AccountSettingsContext.Provider>
  );
};
